import Keycloak from 'keycloak-js'
import store from '../redux/Store'
import { setRoles } from '../redux/reducers/UserRolesSlice'
import { getUserAccessRoles } from '../services/administration/UserService'
import { setMasterNavigation } from '../redux/reducers/MasterNavigationSlice'
import {
  setActiveTenant,
  setAllowedTenants,
} from '../redux/reducers/TenantSlice'
import { logger } from './logger'

const storeToken = (token: string | undefined | null) => {
  sessionStorage.setItem('react-token', token ?? '')
}

const keycloak = new Keycloak({
  url: `${import.meta.env.VITE_APP_KEYCLOAK_URL}/auth`,
  realm: import.meta.env.VITE_APP_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_APP_KEYCLOAK_CLIENT_ID,
})

const initKeycloak = (onAuthenticatedCallback: any) => {
  keycloak
    .init({
      onLoad: 'check-sso',
      // silentCheckSsoRedirectUri:
      //   window.location.origin + '/silent-check-sso.html',
      checkLoginIframe: true,
      checkLoginIframeInterval: 5, // Check every 5 seconds
      pkceMethod: 'S256',
    })
    .then(async (authenticated: any) => {
      if (authenticated) {
        const token: any = keycloak?.token
        sessionStorage.setItem('react-token', token)
        const userAccessRoles = await getUserAccessRoles(token)
        store.dispatch(setRoles(userAccessRoles?.data?.accessList || []))

        // Wrap navigation in the expected format if it's an array
        const navData = userAccessRoles?.data?.navigation || []
        store.dispatch(setMasterNavigation({ navigation: navData }))
        store.dispatch(
          setActiveTenant(userAccessRoles?.data?.tenant?.active || null)
        )
        store.dispatch(
          setAllowedTenants(userAccessRoles?.data?.tenant?.allowed || [])
        )
        onAuthenticatedCallback()
      } else {
        doLogin()
      }
    })
    .catch((err: any) => {
      logger.error('Keycloak initialization error:', err)
    })
}

const doLogin = keycloak?.login?.bind(keycloak)

const doLogout = keycloak?.logout?.bind(keycloak)

const getToken = () => keycloak?.token

const getTokenParsed = () => keycloak?.tokenParsed

const isLoggedIn = () => !!keycloak?.token

const updateToken = (successCallback?: any) =>
  keycloak?.updateToken(30)?.then(successCallback)?.catch(doLogin)

const getUsername = () => keycloak?.tokenParsed?.preferred_username

const getFullname = () =>
  (
    (keycloak?.tokenParsed?.given_name ?? '') +
    ' ' +
    (keycloak?.tokenParsed?.family_name ?? '')
  ).trim()

const getSub = () => keycloak?.tokenParsed?.sub

const getUserEmail = () => keycloak?.tokenParsed?.email

const hasRole = (roles: string[]) =>
  roles.some((role: string) => keycloak?.hasResourceRole(role))

const getLocation = () => keycloak?.tokenParsed?.location

keycloak.onTokenExpired = () => {
  keycloak.updateToken(30).catch(doLogout)
}

keycloak.onAuthError = () => {
  doLogout()
}

keycloak.onAuthSuccess = () => {
  storeToken(keycloak?.token)
}

keycloak.onAuthRefreshSuccess = () => {
  storeToken(keycloak?.token)
}

keycloak.onAuthLogout = () => {
  sessionStorage.clear()
  store.dispatch(setRoles([]))
}

const KeycloakService = {
  storeToken,
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  getFullname,
  getSub,
  getUserEmail,
  hasRole,
  getLocation,
  getTokenParsed,
}

export default KeycloakService
